// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskV2ReviewWorkspace.test.ts
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useState } from "react";

import {
  ASK_V2_CHAT_PATH,
  ASK_V2_PROJECT_PATH,
  buildAskV2ChatReviewPath,
  buildAskV2ProjectReviewPath,
} from "../paths";

export type AskV2ReviewLeftPane = "comments" | "feed";

export type AskV2ReviewWorkspaceScope =
  | { kind: "project"; projectId: string }
  | { kind: "chat"; chatId: string };

export function useAskV2ReviewWorkspace(options: {
  scope: AskV2ReviewWorkspaceScope;
  reviewId?: string | null;
  commentId?: string | null;
  compareRequested?: boolean;
}) {
  const navigate = useNavigate();
  const { scope, reviewId = null, commentId = null } = options;
  const [leftPane, setLeftPaneState] =
    useState<AskV2ReviewLeftPane>("comments");

  const isReviewOpen = reviewId != null && reviewId.length > 0;
  // `?compare=1` on a review link (the route reads it) asks the workspace to
  // open the version comparison once it can; the chat's version check card
  // links this way.
  const compareRequested = isReviewOpen && options.compareRequested === true;

  const openReview = useCallback(
    (nextReviewId: string, openOptions?: { compare?: boolean }) => {
      setLeftPaneState("comments");
      const location =
        scope.kind === "project"
          ? buildAskV2ProjectReviewPath(scope.projectId, nextReviewId)
          : buildAskV2ChatReviewPath(scope.chatId, nextReviewId);
      navigate(
        (openOptions?.compare
          ? { ...location, search: { compare: 1 } }
          : location) as never,
      );
    },
    [navigate, scope],
  );

  /** Drops `?compare=1` once the comparison is open, so a reload does not reopen it. */
  const consumeCompareRequest = useCallback(() => {
    if (!reviewId) return;
    const location =
      scope.kind === "project"
        ? buildAskV2ProjectReviewPath(
            scope.projectId,
            reviewId,
            commentId ?? undefined,
          )
        : buildAskV2ChatReviewPath(
            scope.chatId,
            reviewId,
            commentId ?? undefined,
          );
    navigate({ ...location, replace: true } as never);
  }, [commentId, navigate, reviewId, scope]);

  const closeReview = useCallback(() => {
    setLeftPaneState("comments");
    navigate({
      to: scope.kind === "project" ? ASK_V2_PROJECT_PATH : ASK_V2_CHAT_PATH,
      params:
        scope.kind === "project"
          ? { projectId: scope.projectId }
          : { chatId: scope.chatId },
    });
  }, [navigate, scope]);

  const selectComment = useCallback(
    (nextCommentId: string) => {
      if (!reviewId) return;
      const location =
        scope.kind === "project"
          ? buildAskV2ProjectReviewPath(
              scope.projectId,
              reviewId,
              nextCommentId,
            )
          : buildAskV2ChatReviewPath(scope.chatId, reviewId, nextCommentId);
      navigate(location as never);
    },
    [navigate, reviewId, scope],
  );

  const clearComment = useCallback(() => {
    if (!reviewId) return;
    openReview(reviewId);
  }, [openReview, reviewId]);

  const switchReview = useCallback(
    (nextReviewId: string) => {
      openReview(nextReviewId);
    },
    [openReview],
  );

  const setLeftPane = useCallback((pane: AskV2ReviewLeftPane) => {
    setLeftPaneState(pane);
  }, []);

  return useMemo(
    () => ({
      isReviewOpen,
      reviewId,
      commentId,
      leftPane,
      openReview,
      compareRequested,
      consumeCompareRequest,
      closeReview,
      selectComment,
      clearComment,
      switchReview,
      setLeftPane,
    }),
    [
      clearComment,
      closeReview,
      commentId,
      compareRequested,
      consumeCompareRequest,
      isReviewOpen,
      leftPane,
      openReview,
      reviewId,
      selectComment,
      setLeftPane,
      switchReview,
    ],
  );
}
